import { useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { useSelectedLocation } from "../../../hooks";
import { createApiClient, apiClient } from "../../../api/apiClient";
import { DropdownItem } from "../../../types/observation";
import { toSparcWalkRowData } from "../utils/sparcWalkDataTransformers";
import { SparcWalkRowData } from "../../../types/sparcWalk";
import {
  handleSparcWalkErrorWithFriendlyMessage,
  SPARC_WALK_API_NAMES,
} from "../../../store/utils/sparcWalkErrorHandler";

interface UseSparcWalkDataProps {
  setSparcWalkData: (data: SparcWalkRowData[]) => void;
  setFullSparcWalkData: (data: SparcWalkRowData[]) => void;
  setIsLoading: (loading: boolean) => void;
  setApiError: (error: string | null) => void;
}

/**
 * Custom hook to fetch SPARC Walk data
 * Handles data fetching logic outside the component
 */
const useSparcWalkData = ({
  setSparcWalkData,
  setFullSparcWalkData,
  setIsLoading,
  setApiError,
}: UseSparcWalkDataProps) => {
  const { walkType, hiddenWalk } = useSelector(
    (state: RootState) => state.sparcWalk
  );
  const selectedLocation = useSelectedLocation();
  const page = 1;

  const fetchSparcWalkData = useCallback(
    async (showLoader: boolean = true) => {
      try {
        if (showLoader) setIsLoading(true);

        if (!selectedLocation) {
          setApiError("No location selected");
          return;
        }

        const location = encodeURIComponent(
          (selectedLocation as any)?.displayText || (selectedLocation as any)?.name || "Hayward CA"
        );
        const endpoint = `/sparc-walks?location=${location}&walkType=${encodeURIComponent(
          walkType
        )}&hiddenWalk=${encodeURIComponent(
          hiddenWalk
        )}&page=${page}&pageSize=100`;

        console.log("Fetching sparc walks without filters:", endpoint);

        const api = createApiClient();
        const json = await apiClient(api, endpoint, "GET");

        if (!json.data || !Array.isArray(json.data)) {
          throw new Error("Invalid data structure received");
        }

        const formattedData = json.data.map(toSparcWalkRowData);
        setFullSparcWalkData(formattedData);
        setSparcWalkData(formattedData);
        setApiError(null);
      } catch (err) {
        console.error("Error fetching sparc walk:", err);
        
        // Use centralized error handler with modal overlay
        handleSparcWalkErrorWithFriendlyMessage(
          SPARC_WALK_API_NAMES.FETCH_WALKS,
          err,
          true, // Show modal overlay
          {
            location: selectedLocation,
            walkType,
            hiddenWalk,
          }
        );
        
        setApiError(
          err instanceof Error ? err.message : "Failed to fetch sparc walk data"
        );
        setSparcWalkData([]);
      } finally {
        if (showLoader) setIsLoading(false);
      }
    },
    [
      selectedLocation,
      walkType,
      hiddenWalk,
      page,
      setIsLoading,
      setApiError,
      setSparcWalkData,
      setFullSparcWalkData,
    ]
  );

  return {
    fetchSparcWalkData,
    selectedLocation,
    walkType,
    hiddenWalk,
  };
};

export default useSparcWalkData;
